import * as React from 'react';
import {
  OutcomeReason,
  SubmissionStatus,
  type OutcomeReason as OutcomeReasonCode,
  type SubmissionStatus as SubmissionStatusCode,
} from '@clawhire/shared';
import { enumOptions } from '@/lib/format';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleSelect } from '@/components/ui/select';

export interface SubmissionStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateLabel: string;
  status: SubmissionStatusCode | null;
  busy?: boolean;
  onConfirm: (outcomeReason: OutcomeReasonCode, note: string | undefined) => void;
}

const DEFAULT_REASON: Partial<Record<SubmissionStatusCode, OutcomeReasonCode>> = {
  PLACED: 'PLACED',
  WITHDRAWN: 'CANDIDATE_WITHDREW',
};

/** Rejected / Withdrawn / Placed need an outcome reason (spec §6.2). */
export function SubmissionStatusDialog({
  open,
  onOpenChange,
  candidateLabel,
  status,
  busy,
  onConfirm,
}: SubmissionStatusDialogProps) {
  const [reason, setReason] = React.useState<OutcomeReasonCode | null>(null);
  const [note, setNote] = React.useState('');
  React.useEffect(() => {
    if (open) {
      setReason(status ? (DEFAULT_REASON[status] ?? null) : null);
      setNote('');
    }
  }, [open, status]);
  const label = status ? SubmissionStatus.label(status) : '';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason) onConfirm(reason, note.trim() || undefined);
          }}
        >
          <DialogHeader>
            <DialogTitle>
              Mark {candidateLabel} as {label}
            </DialogTitle>
            <DialogDescription>An outcome reason is required for {label}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="status-outcome-reason">Outcome reason</Label>
            <SimpleSelect
              id="status-outcome-reason"
              value={reason}
              options={enumOptions(OutcomeReason)}
              onChange={(v) => setReason(v as OutcomeReasonCode | null)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="status-note">Note (optional)</Label>
            <Input id="status-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!reason} loading={busy}>
              Confirm
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
